import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { signOut, updateProfile } from 'firebase/auth';
import { auth, db } from '../firebase';
import { initials, sellerDisplayName } from '../lib/format';
import Icon from '../components/Icon';
import { useSheets } from '../hooks/useSheets';

export default function AccountPage({ user, profile }) {
  const nav = useNavigate();
  const { openSaved } = useSheets();
  const editTitleId = useId();
  const signOutTitleId = useId();
  const inputRef = useRef(null);
  const [editOpen, setEditOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const name = sellerDisplayName(profile || { displayName: user.displayName }, 'Your profile');
  const modalOpen = editOpen || signOutOpen;

  useEffect(() => {
    if (!modalOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || busy) return;
      setEditOpen(false);
      setSignOutOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [modalOpen, busy]);

  useEffect(() => {
    if (!editOpen) return undefined;
    setDisplayName(profile?.displayName || user.displayName || '');
    setError('');
    const t = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [editOpen, profile, user]);

  const save = async (e) => {
    e.preventDefault();
    const next = displayName.trim();
    if (!next) {
      setError('Enter a display name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await updateProfile(user, { displayName: next });
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: next,
        updatedAt: serverTimestamp(),
      });
      setEditOpen(false);
    } catch (err) {
      setError(err.message || 'Could not save name.');
    } finally {
      setBusy(false);
    }
  };

  const confirmSignOut = async () => {
    setBusy(true);
    try {
      await signOut(auth);
    } finally {
      setBusy(false);
      setSignOutOpen(false);
    }
  };

  return (
    <section className="content-page account">
      <header className="account-hero">
        <div className="profile-avatar account-hero__avatar">
          {profile?.photoUrl || user.photoURL ? (
            <img src={profile?.photoUrl || user.photoURL} alt="" />
          ) : (
            initials(name)
          )}
        </div>
        <div className="account-hero__copy">
          <h1>{name}</h1>
          <p>{user.email}</p>
        </div>
      </header>

      <div className="account-links">
        <p className="account-section-label">Account</p>
        <div className="account-card">
          <button type="button" onClick={() => setEditOpen(true)}>
            <span className="account-card__icon" aria-hidden="true">
              <Icon name="edit" size={18} />
            </span>
            <span>
              <b>Edit display name</b>
              <small>How you appear on listings and chats</small>
            </span>
            <Icon name="chevron" size={18} />
          </button>
          <button type="button" onClick={() => nav('/sell')}>
            <span className="account-card__icon" aria-hidden="true">
              <Icon name="tag" size={18} />
            </span>
            <span>
              <b>My listings</b>
              <small>Create and manage what you’re selling</small>
            </span>
            <Icon name="chevron" size={18} />
          </button>
          <button type="button" onClick={() => openSaved()}>
            <span className="account-card__icon" aria-hidden="true">
              <Icon name="bookmark" size={18} />
            </span>
            <span>
              <b>Saved</b>
              <small>Listings you’ve bookmarked</small>
            </span>
            <Icon name="chevron" size={18} />
          </button>
        </div>
      </div>

      <button type="button" className="sign-out" onClick={() => setSignOutOpen(true)}>
        <Icon name="logout" size={18} /> Sign out
      </button>

      {editOpen ? (
        <div className="account-modal-root" role="presentation">
          <button
            type="button"
            className="account-modal-backdrop"
            aria-label="Close"
            onClick={() => !busy && setEditOpen(false)}
          />
          <div
            className="account-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={editTitleId}
          >
            <h2 id={editTitleId}>Edit display name</h2>
            <p className="account-modal__sub">This is how you show up on listings and in chats.</p>
            <form onSubmit={save} className="account-modal__form">
              <label className="field">
                <input
                  ref={inputRef}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Display name"
                  aria-label="Display name"
                  autoComplete="name"
                  disabled={busy}
                />
              </label>
              {error ? <p className="form-error">{error}</p> : null}
              <div className="account-modal__actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setEditOpen(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {signOutOpen ? (
        <div className="account-modal-root" role="presentation">
          <button
            type="button"
            className="account-modal-backdrop"
            aria-label="Close"
            onClick={() => !busy && setSignOutOpen(false)}
          />
          <div
            className="account-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby={signOutTitleId}
          >
            <h2 id={signOutTitleId}>Sign out?</h2>
            <p className="account-modal__sub">You’ll need to sign in again to browse and message.</p>
            <div className="account-modal__actions account-modal__actions--spaced">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setSignOutOpen(false)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="danger-button"
                onClick={confirmSignOut}
                disabled={busy}
              >
                {busy ? 'Signing out…' : 'Sign out'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
